// Sprint 6 JOB-FEAT-01 — PM Assignment. Backend: api-service app/api/v1/endpoints/jobs.py,
// app/domain/jobs/service.py (JobService.list_eligible_pms/assign_pm).
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}

export interface QuickBooksStatus {
  /** reconnect_required = expired / revoked: sync is paused (and resumes by itself once reconnected). */
  state?: 'not_connected' | 'connected' | 'reconnect_required';
  sync_enabled?: boolean;
  issue?: string | null;
  connected: boolean;
  realm_id?: string | null;
  environment?: string | null;
  company_name?: string | null;
  refresh_token_expires_at?: string | null;
}

export interface QuickBooksSyncSettings {
  sync_enabled: boolean | null;
  service_item_name: string | null;
  income_account_name: string | null;
  tax_code_map: Record<string, string> | null;
  payment_method_map: Record<string, string> | null;
  platform: {
    supported_operations?: string[];
    sync_statuses?: string[];
    reconciliation_reasons?: string[];
    field_mappings?: Record<string, Record<string, string>>;
  };
}

export interface QuickBooksSyncOptions {
  items: { id: string; name: string }[];
  income_accounts: { id: string; name: string }[];
  tax_codes: { id: string; name: string }[];
  payment_methods: { id: string; name: string }[];
}

export type QuickBooksSyncSettingsInput = Partial<Pick<QuickBooksSyncSettings,
  'sync_enabled' | 'service_item_name' | 'income_account_name' | 'tax_code_map' | 'payment_method_map'>>;

export interface EligiblePM {
  user_id: string;
  name: string;
  email?: string | null;
  active_workload: number;
  is_recommended: boolean;
}

export interface AssignPmInput {
  engagementId: string;
  pm_user_id: string;
  notify_customer?: boolean;
}

// JOB-FEAT-02 Job Card — mirrors app/domain/jobs/job_card.py's aggregation shape.
export interface JobCardMaterialLine {
  estimate_line_id: string;
  item_code?: string | null;
  description?: string | null;
  product_code?: string | null;
  supplier_product_code?: string | null;
  quantity?: number | null;
  order_quantity?: number | null;
  notes?: string | null;
  uom?: string | null;
  is_custom?: boolean;
}

export interface JobCardOpenTask {
  task_id: string;
  title?: string | null;
  status?: string | null;
  due_on?: string | null;
}

export interface JobCard {
  engagement_id: string;
  property: { property_id?: string; address_line_1?: string; city?: string; state?: string } | null;
  project_manager_id?: string | null;
  project_manager_name?: string | null;
  project_manager_phone?: string | null;
  pm_assigned_by_name?: string | null;
  pm_assigned_on?: string | null;
  sales_agent_id?: string | null;
  accepted_option_label?: string | null;
  accepted_proposal_number?: string | null;
  accepted_proposal_date?: string | null;
  contract_value?: number | null;
  contract_value_restricted?: boolean;
  materials_by_supplier: Record<string, JobCardMaterialLine[]>;
  open_tasks: JobCardOpenTask[];
  crew: { crew_id: string; name: string } | null;
  crew_lead_name?: string | null;
  crew_lead_phone?: string | null;
  execution_started_at?: string | null;
  field_work_completed_at?: string | null;
  qa_result_summary?: 'pass' | 'fail' | null;
  qa_passed_at?: string | null;
  materials_delivery_note?: string | null;
  schedule: {
    scheduled_date?: string | null; start_time?: string | null;
    duration_minutes?: number | null; customer_window_note?: string | null;
  } | null;
  weather: unknown | null;
}

// JOB-FEAT-03 Crew & Job Scheduling.
export interface Crew {
  crew_id: string;
  tenant_id: string;
  name: string;
  team_type: 'internal' | 'contract';
  crew_lead_user_id?: string | null;
  member_user_ids: string[];
  contact_name?: string | null;
  contact_phone?: string | null;
  color?: string | null;
  is_active: boolean;
}

export interface CrewBody {
  name: string;
  team_type?: 'internal' | 'contract';
  crew_lead_user_id?: string | null;
  member_user_ids?: string[];
  contact_name?: string | null;
  contact_phone?: string | null;
  color?: string | null;
}

export interface JobScheduleConflict {
  engagement_id: string;
  scheduled_date: string;
  start_time: string;
  duration_minutes: number;
}

export interface ScheduleHistoryEntry {
  scheduled_date?: string | null;
  start_time?: string | null;
  duration_minutes?: number | null;
  crew_id?: string | null;
  reason?: string | null;
  actor_id: string;
  actor_name?: string | null;
  changed_on: string;
  event_type?: 'scheduled' | 'rescheduled' | null;
}

export interface JobSchedule {
  engagement_id: string;
  tenant_id: string;
  scheduled_date: string;
  start_time: string;
  duration_minutes: number;
  crew_id: string;
  customer_window_note?: string | null;
  history?: ScheduleHistoryEntry[];
}

export interface ScheduleResult {
  schedule: JobSchedule;
  conflicts: JobScheduleConflict[];
  blocked: boolean;
}

export interface ScheduleJobInput {
  engagementId: string;
  scheduled_date: string;
  start_time: string;
  duration_minutes: number;
  crew_id: string;
  customer_window_note?: string;
  reason?: string;
  acknowledge_conflict?: boolean;
}

// JOB-FEAT-04 Project Board.
export interface ProjectBoardEntry {
  entry_id: string;
  engagement_id: string;
  entry_type: 'note' | 'photo' | 'issue' | 'status_event';
  entry_subtype?: string | null;
  author_user_id?: string | null;
  author_name?: string | null;
  /** The poster's role name when they posted (role tag); null for system and older posts. */
  author_role?: string | null;
  /** user | sam (posted through SAM for the author) | system (workflow hook). */
  source?: 'user' | 'sam' | 'system';
  body: string;
  media_urls: string[];
  seen_by: string[];
  likes: string[];
  mention_user_ids?: string[];
  created_on: string;
}

export interface PostProjectBoardEntryInput {
  engagementId: string;
  entry_type: 'note' | 'photo' | 'issue';
  entry_subtype?: string;
  body: string;
  media_urls?: string[];
  mention_user_ids?: string[];
}

export interface BoardEntryActionInput {
  engagementId: string;
  entryId: string;
}

// JOB-FEAT-05 QA Summary.
export interface QaSummary {
  qa_result: 'pass' | 'fail' | null;
  inspected_by_name?: string | null;
  inspected_at?: string | null;
  reason?: string | null;
  qa_checklist_task: { task_id: string; status: string; title: string } | null;
}

// JOB-FEAT-03 Scheduling calendar — mirrors app/domain/jobs/scheduling_calendar.py's shape.
export interface CalendarCrew {
  crew_id: string;
  name: string;
  crew_lead_user_id?: string | null;
  color: string;
}

export interface CalendarDayBooking {
  crew_id: string;
  engagement_id: string;
  label: string;
  color: string;
}

export interface CalendarDay {
  date: string;
  weekday: number;
  weather: { short_forecast?: string | null; temperature?: number | null; precip_chance?: number | null } | null;
  crews_free_count: number;
  bookings: CalendarDayBooking[];
  selected_crew_busy: boolean;
  customer_unavailable_reason?: string | null;
  customer_no_work: boolean;
  customer_preferred: boolean;
}

export interface SchedulingCalendar {
  year: number;
  month: number;
  crews: CalendarCrew[];
  days: CalendarDay[];
  customer_window_note?: string | null;
}

// Billing Settlement — invoice records stored in engagement.data.invoices
export interface InvoicePayment {
  amount: number;
  date: string;
  method: string;
  reference?: string | null;
  recorded_by?: string;
  /** 'quickbooks' = it arrived in QuickBooks; reversed = it was reversed there (kept for audit, no longer counted). */
  source?: string;
  reversed?: boolean;
  reversed_reason?: string;
}

/** Sync statuses (platform vocabulary `quickbooks_sync.sync_statuses`). */
export type QuickBooksSyncStatus =
  'pending' | 'synced' | 'failed' | 'held_for_review' | 'reconciliation_required' | 'suspended';

export interface QuickBooksPartState {
  status: QuickBooksSyncStatus;
  last_sync?: string;
  error?: string | null;
  cause?: string | null;
  /** Automatic retries so far; the part stays Failed once the platform limit is reached. */
  attempts?: number;
  next_retry_at?: string | null;
  retry_exhausted?: boolean;
}

/** The QuickBooks mirror of an invoice (only present once the tenant has connected QuickBooks). */
export interface InvoiceQuickBooks {
  customer_id?: string;
  invoice?: QuickBooksPartState & {
    qb_invoice_id?: string | null;
    doc_number?: string | null;
    voided?: boolean;
    adopted_existing?: boolean;
  };
  payments?: QuickBooksPartState & { qb_payment_ids?: string[] };
  /** Open while Smart Contractor and QuickBooks disagree; automatic sync is paused until resolved. */
  reconciliation?: {
    reason: string;
    detail: string;
    detected_at: string;
    resolved: boolean;
    resolution?: string;
    resolved_by?: string;
    note?: string;
    also?: string[];
  };
  /** The invoice was unlinked from QuickBooks (a QuickBooks-side change was accepted) — it never syncs again. */
  unlinked?: boolean;
  /** Changes seen in QuickBooks after Billing Settled — recorded only, nothing was modified. */
  activity?: { at: string; kind: string; detail: string; material: boolean }[];
}

export interface QuickBooksSummary {
  status: QuickBooksSyncStatus;
  parts: Record<string, QuickBooksSyncStatus>;
  issue: string | null;
  unlinked: boolean;
  qb_invoice?: string | null;
}

export interface InvoiceVersion {
  version: number;
  state: 'frozen';
  frozen_at: string;
  frozen_by: string;
  return_reason?: string | null;
  amount?: number;
}

export interface InvoiceValidation {
  mode: 'warn' | 'block' | 'off';
  accepted_value: number | null;
  previously_invoiced: number;
  this_invoice: number;
  cumulative: number;
  over_by: number;
  within_value: boolean;
  outcome: 'within' | 'warned' | 'blocked' | 'no_accepted_value' | 'not_checked';
}

export interface InvoiceHistoryEntry {
  at: string;
  by: string;
  /** Who did it (absent on older entries and system actors) — the client shows "Me" when it is the viewer. */
  by_user_id?: string | null;
  event: string;
  version: number;
  reason?: string;
}

/** The invoice lifecycle: Prepared → Approved → Sent → Partially Paid → Paid, with Returned and
 *  Cancelled off the main path. Overdue is NOT a status — see Invoice.is_overdue. */
export type InvoiceStatus =
  | 'Prepared' | 'Approved' | 'Sent' | 'Partially Paid' | 'Paid' | 'Returned' | 'Cancelled';

export type InvoiceActionCode =
  'approve' | 'return' | 'resubmit' | 'send' | 'resend' | 'record_payment' | 'cancel' | 'retry_quickbooks_sync' | 'resolve_quickbooks';

/** Which invoice actions the caller's role may perform — keyed by the workflow definition's
 * authority-rule codes (invoice_prepare / invoice_review / ...). */
export type InvoicePermissions = Record<
  'invoice_view_amounts' | 'invoice_prepare' | 'invoice_review' | 'invoice_send' | 'invoice_record_payment' | 'invoice_cancel',
  boolean
>;

export interface Invoice {
  invoice_id: string;
  number: string;
  type: string;
  payment_terms: string;
  due_date?: string | null;
  lines: Record<string, unknown>[];
  additional_lines: Record<string, unknown>[];
  subtotal: number;
  tax: number;
  previously_invoiced: number;
  amount: number;
  paid: number;
  status: InvoiceStatus;
  created_at: string;
  created_by: string;
  quickbooks_ref?: string | null;
  payments: InvoicePayment[];
  version?: number;
  balance?: number;
  is_overdue?: boolean;
  overdue_days?: number;
  approved_by?: string;
  approved_at?: string;
  return_reason?: string | null;
  returned_by?: string;
  returned_at?: string;
  cancel_reason?: string;
  cancelled_by?: string;
  cancelled_at?: string;
  locked_at?: string | null;
  /** Earlier versions frozen when the invoice was returned (kept for audit). */
  versions?: InvoiceVersion[];
  /** Cumulative-invoicing check recorded when the invoice was prepared / resubmitted. */
  validation?: InvoiceValidation;
  quickbooks?: InvoiceQuickBooks;
  quickbooks_summary?: QuickBooksSummary | null;
  /** Overdue reminders sent to the customer. */
  reminders?: { at: string; channel: string; recipient: string; days_overdue: number; balance: number }[];
  sent_by?: string;
  sent_at?: string;
  history?: InvoiceHistoryEntry[];
  /** True when the caller's role may not see money — amounts/lines/payments are stripped. */
  restricted?: boolean;
}

export interface InvoiceActionInput {
  engagementId: string;
  invoiceId: string;
  action: InvoiceActionCode;
  reason?: string;
  payment_amount?: number;
  payment_date?: string;
  payment_method?: string;
  payment_reference?: string;
  /** retry_quickbooks_sync only: the QuickBooks customer a person confirmed for an ambiguous match. */
  qb_customer_id?: string;
  /** resolve_quickbooks: how the QuickBooks difference was settled. */
  resolution?: 'resync' | 'accept_quickbooks_change';
}

/** One invoiceable particular of what the customer accepted, with its invoicing state. */
export interface InvoicePrepareLine {
  line_id: string;
  name: string;
  unit: string;
  quantity: number;
  unit_price: number | null;
  amount: number;
  tax_code: string;
  group: 'materials' | 'services' | 'adjustment';
  /** Numbers of the active invoices that already took any of this line. */
  invoiced_on: string[];
  /** A line can be invoiced in parts (6 of 12 bundles now, the rest later). */
  invoiced_quantity: number;
  remaining_quantity: number;
  /** What is left — the last part closes the line exactly, with no rounding drift. */
  remaining_amount: number;
  fully_invoiced: boolean;
}

export interface InvoicePrepareContext {
  tax_codes: string[];
  validation_mode: 'warn' | 'block' | 'off';
  previous_invoices: { number: string; amount: number; status: string }[];
  previously_invoiced: number;
  /** Accepted commercial value, tax included; null when nothing has been accepted/priced. */
  accepted_value: number | null;
  tax_percent: number;
  lines: InvoicePrepareLine[];
}

export interface CreateInvoiceInput {
  engagementId: string;
  invoice_type?: string;
  payment_terms?: string;
  due_date?: string;
  lines?: Record<string, unknown>[];
  additional_lines?: Record<string, unknown>[];
  subtotal?: number;
  tax?: number;
  previously_invoiced?: number;
  amount?: number;
}

export interface UpdateInvoiceInput {
  engagementId: string;
  invoiceId: string;
  quickbooks_ref?: string;
}

export const jobsApi = createApi({
  reducerPath: 'jobsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['EligiblePMs', 'JobCard', 'Crews', 'Schedule', 'ProjectBoard', 'QaSummary', 'SchedulingCalendar', 'Invoices', 'QuickBooks'],
  endpoints: (builder) => ({
    // QuickBooks Online connection (Settings -> Integrations). The tenant admin authorises on the
    // Intuit page: `authorize` returns the URL, and the callback lands back on the Settings page.
    getQuickBooksStatus: builder.query<QuickBooksStatus, void>({
      query: () => '/api/v1/quickbooks/status',
      transformResponse: (res: ApiResponse<QuickBooksStatus> | QuickBooksStatus) => ('data' in res ? res.data : res),
      providesTags: ['QuickBooks'],
    }),
    getQuickBooksAuthorizeUrl: builder.query<string, void>({
      // credentials: the response sets the HttpOnly cookie that binds the OAuth state to this browser;
      // the Intuit redirect to /callback must carry it back or the connection is refused.
      query: () => ({ url: '/api/v1/quickbooks/authorize', credentials: 'include' }),
      transformResponse: (res: ApiResponse<{ authorization_url: string }> | { authorization_url: string }) =>
        ('data' in res ? res.data : res).authorization_url,
    }),
    getQuickBooksSyncSettings: builder.query<QuickBooksSyncSettings, void>({
      query: () => '/api/v1/quickbooks/sync-settings',
      transformResponse: (res: ApiResponse<QuickBooksSyncSettings>) => res.data,
      providesTags: ['QuickBooks'],
    }),
    getQuickBooksSyncOptions: builder.query<QuickBooksSyncOptions, void>({
      query: () => '/api/v1/quickbooks/sync-options',
      transformResponse: (res: ApiResponse<QuickBooksSyncOptions>) => res.data,
      providesTags: ['QuickBooks'],
    }),
    saveQuickBooksSyncSettings: builder.mutation<QuickBooksSyncSettings, QuickBooksSyncSettingsInput>({
      query: (body) => ({ url: '/api/v1/quickbooks/sync-settings', method: 'PUT', body }),
      transformResponse: (res: ApiResponse<QuickBooksSyncSettings>) => res.data,
      invalidatesTags: ['QuickBooks'],
    }),
    disconnectQuickBooks: builder.mutation<void, void>({
      query: () => ({ url: '/api/v1/quickbooks/disconnect', method: 'DELETE' }),
      invalidatesTags: ['QuickBooks'],
    }),
    getEligiblePms: builder.query<EligiblePM[], string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/eligible-pms`,
      transformResponse: (res: ApiResponse<EligiblePM[]>) => res.data,
      providesTags: ['EligiblePMs'],
    }),
    assignPm: builder.mutation<Record<string, unknown>, AssignPmInput>({
      query: ({ engagementId, pm_user_id, notify_customer }) => ({
        url: `/api/v1/engagements/${engagementId}/assign-pm`,
        method: 'POST',
        body: { pm_user_id, notify_customer: !!notify_customer },
      }),
      transformResponse: (res: ApiResponse<Record<string, unknown>>) => res.data,
      invalidatesTags: ['EligiblePMs'],
    }),
    getJobCard: builder.query<JobCard, string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/job-card`,
      transformResponse: (res: ApiResponse<JobCard>) => res.data,
      providesTags: (_r, _e, engagementId) => [{ type: 'JobCard' as const, id: engagementId }],
    }),
    listCrews: builder.query<Crew[], void>({
      query: () => '/api/v1/jobs/crews',
      transformResponse: (res: ApiResponse<Crew[]>) => res.data,
      providesTags: ['Crews'],
    }),
    createCrew: builder.mutation<Crew, CrewBody>({
      query: (body) => ({ url: '/api/v1/jobs/crews', method: 'POST', body }),
      transformResponse: (res: ApiResponse<Crew>) => res.data,
      invalidatesTags: ['Crews'],
    }),
    updateCrew: builder.mutation<Crew, { crew_id: string } & CrewBody>({
      query: ({ crew_id, ...body }) => ({ url: `/api/v1/jobs/crews/${crew_id}`, method: 'PATCH', body }),
      transformResponse: (res: ApiResponse<Crew>) => res.data,
      invalidatesTags: ['Crews'],
    }),
    deleteCrew: builder.mutation<void, string>({
      query: (crew_id) => ({ url: `/api/v1/jobs/crews/${crew_id}`, method: 'DELETE' }),
      invalidatesTags: ['Crews'],
    }),
    getSchedule: builder.query<JobSchedule | null, string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/schedule`,
      transformResponse: (res: ApiResponse<JobSchedule | null>) => res.data,
      providesTags: (_r, _e, engagementId) => [{ type: 'Schedule' as const, id: engagementId }],
    }),
    scheduleJob: builder.mutation<ScheduleResult, ScheduleJobInput>({
      query: ({ engagementId, ...body }) => ({
        url: `/api/v1/engagements/${engagementId}/schedule`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: ApiResponse<ScheduleResult>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [
        { type: 'Schedule' as const, id: engagementId },
        { type: 'JobCard' as const, id: engagementId },
      ],
    }),
    getProjectBoard: builder.query<{ entries: ProjectBoardEntry[]; total: number; unread: number }, string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/project-board`,
      transformResponse: (res: ApiResponse<{ entries: ProjectBoardEntry[]; total: number; unread: number }>) => res.data,
      providesTags: (_r, _e, engagementId) => [{ type: 'ProjectBoard' as const, id: engagementId }],
    }),
    postProjectBoardEntry: builder.mutation<ProjectBoardEntry, PostProjectBoardEntryInput>({
      query: ({ engagementId, ...body }) => ({
        url: `/api/v1/engagements/${engagementId}/project-board`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: ApiResponse<ProjectBoardEntry>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'ProjectBoard' as const, id: engagementId }],
    }),
    uploadProjectBoardMedia: builder.mutation<{ urls: string[] }, { engagementId: string; files: File[] }>({
      queryFn: async ({ engagementId, files }, _api, _extraOptions, baseQuery) => {
        const formData = new FormData();
        files.forEach((f) => formData.append('files', f));
        const result = await baseQuery({
          url: `/api/v1/engagements/${engagementId}/project-board/media`,
          method: 'POST',
          body: formData,
        });
        if (result.error) return { error: result.error };
        return { data: (result.data as { data: { urls: string[] } }).data };
      },
    }),
    markBoardEntrySeen: builder.mutation<ProjectBoardEntry, BoardEntryActionInput>({
      query: ({ engagementId, entryId }) => ({
        url: `/api/v1/engagements/${engagementId}/project-board/${entryId}/seen`,
        method: 'POST',
      }),
      transformResponse: (res: ApiResponse<ProjectBoardEntry>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'ProjectBoard' as const, id: engagementId }],
    }),
    toggleBoardEntryLike: builder.mutation<ProjectBoardEntry, BoardEntryActionInput>({
      query: ({ engagementId, entryId }) => ({
        url: `/api/v1/engagements/${engagementId}/project-board/${entryId}/like`,
        method: 'POST',
      }),
      transformResponse: (res: ApiResponse<ProjectBoardEntry>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'ProjectBoard' as const, id: engagementId }],
    }),
    deleteBoardEntry: builder.mutation<ProjectBoardEntry, BoardEntryActionInput>({
      query: ({ engagementId, entryId }) => ({
        url: `/api/v1/engagements/${engagementId}/project-board/${entryId}`,
        method: 'DELETE',
      }),
      transformResponse: (res: ApiResponse<ProjectBoardEntry>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'ProjectBoard' as const, id: engagementId }],
    }),
    getQaSummary: builder.query<QaSummary, string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/qa-summary`,
      transformResponse: (res: ApiResponse<QaSummary>) => res.data,
      providesTags: (_r, _e, engagementId) => [{ type: 'QaSummary' as const, id: engagementId }],
    }),
    setMaterialOrderQuantity: builder.mutation<JobCard, { engagementId: string; estimateLineId: string; order_quantity?: number | null; notes?: string | null }>({
      query: ({ engagementId, estimateLineId, ...body }) => ({
        url: `/api/v1/engagements/${engagementId}/materials/${estimateLineId}/order-quantity`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (res: ApiResponse<JobCard>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'JobCard' as const, id: engagementId }],
    }),
    addCustomMaterialLine: builder.mutation<JobCard, {
      engagementId: string; description: string; item_code?: string; supplier_code?: string;
      order_quantity?: number; uom?: string; notes?: string;
    }>({
      query: ({ engagementId, ...body }) => ({
        url: `/api/v1/engagements/${engagementId}/materials/custom-line`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: ApiResponse<JobCard>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'JobCard' as const, id: engagementId }],
    }),
    notifyCustomerOfHold: builder.mutation<void, { engagementId: string }>({
      query: ({ engagementId }) => ({ url: `/api/v1/engagements/${engagementId}/notify-customer-hold`, method: 'POST' }),
    }),
    updateHoldDetails: builder.mutation<void, { engagementId: string; hold_notes?: string; hold_expected_resume?: string }>({
      query: ({ engagementId, ...body }) => ({ url: `/api/v1/engagements/${engagementId}/hold-details`, method: 'PATCH', body }),
    }),
    getJobCardPdf: builder.query<Blob, string>({
      query: (engagementId) => ({
        url: `/api/v1/engagements/${engagementId}/card/report.pdf`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    failQaWithDefects: builder.mutation<
      { defect_task_ids: string[] },
      { engagementId: string; reason: string; defect_items: { description: string; due_in_days?: number }[] }
    >({
      query: ({ engagementId, ...body }) => ({
        url: `/api/v1/engagements/${engagementId}/qa/fail`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: ApiResponse<{ defect_task_ids: string[] }>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'QaSummary' as const, id: engagementId }],
    }),
    getSchedulingCalendar: builder.query<SchedulingCalendar, { engagementId: string; year: number; month: number; crewId?: string }>({
      query: ({ engagementId, year, month, crewId }) => ({
        url: `/api/v1/engagements/${engagementId}/scheduling-calendar`,
        params: { year, month, ...(crewId ? { crew_id: crewId } : {}) },
      }),
      transformResponse: (res: ApiResponse<SchedulingCalendar>) => res.data,
      providesTags: (_r, _e, { engagementId }) => [{ type: 'SchedulingCalendar' as const, id: engagementId }],
    }),
    listInvoices: builder.query<Invoice[], string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/invoices`,
      transformResponse: (res: ApiResponse<Invoice[]>) => res.data,
      providesTags: (_r, _e, engagementId) => [{ type: 'Invoices' as const, id: engagementId }],
    }),
    createInvoice: builder.mutation<Invoice, CreateInvoiceInput>({
      query: ({ engagementId, ...body }) => ({
        url: `/api/v1/engagements/${engagementId}/invoices`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: ApiResponse<Invoice>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'Invoices' as const, id: engagementId }],
    }),
    getInvoicePrepareContext: builder.query<InvoicePrepareContext, string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/invoices/prepare-context`,
      transformResponse: (res: ApiResponse<InvoicePrepareContext>) => res.data,
      providesTags: (_r, _e, engagementId) => [{ type: 'Invoices' as const, id: engagementId }],
    }),
    getInvoicePermissions: builder.query<InvoicePermissions, string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/invoices/permissions`,
      transformResponse: (res: ApiResponse<InvoicePermissions>) => res.data,
      providesTags: (_r, _e, engagementId) => [{ type: 'Invoices' as const, id: engagementId }],
    }),
    invoiceAction: builder.mutation<Invoice, InvoiceActionInput>({
      query: ({ engagementId, invoiceId, ...body }) => ({
        url: `/api/v1/engagements/${engagementId}/invoices/${invoiceId}/actions`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: ApiResponse<Invoice>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'Invoices' as const, id: engagementId }],
    }),
    updateInvoice: builder.mutation<Invoice, UpdateInvoiceInput>({
      query: ({ engagementId, invoiceId, ...body }) => ({
        url: `/api/v1/engagements/${engagementId}/invoices/${invoiceId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (res: ApiResponse<Invoice>) => res.data,
      invalidatesTags: (_r, _e, { engagementId }) => [{ type: 'Invoices' as const, id: engagementId }],
    }),
  }),
});

export const {
  useGetQuickBooksSyncSettingsQuery, useGetQuickBooksSyncOptionsQuery, useSaveQuickBooksSyncSettingsMutation,
  useGetInvoicePrepareContextQuery,
  useGetQuickBooksStatusQuery, useLazyGetQuickBooksAuthorizeUrlQuery, useDisconnectQuickBooksMutation,
  useGetEligiblePmsQuery, useAssignPmMutation, useGetJobCardQuery,
  useListCrewsQuery, useCreateCrewMutation, useUpdateCrewMutation, useDeleteCrewMutation,
  useGetScheduleQuery, useScheduleJobMutation,
  useGetProjectBoardQuery, usePostProjectBoardEntryMutation, useUploadProjectBoardMediaMutation,
  useMarkBoardEntrySeenMutation, useToggleBoardEntryLikeMutation, useDeleteBoardEntryMutation, useGetQaSummaryQuery,
  useGetSchedulingCalendarQuery, useSetMaterialOrderQuantityMutation, useAddCustomMaterialLineMutation,
  useNotifyCustomerOfHoldMutation, useLazyGetJobCardPdfQuery, useFailQaWithDefectsMutation,
  useUpdateHoldDetailsMutation, useListInvoicesQuery, useCreateInvoiceMutation, useUpdateInvoiceMutation,
  useGetInvoicePermissionsQuery, useInvoiceActionMutation,
} = jobsApi;
